<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-gray-100">Products</h1>
        <p class="mt-1 text-sm text-gray-400">
          Last synced: {{ lastSyncedText }}
        </p>
      </div>
      <div class="flex items-center space-x-4">
        <!-- View Toggle -->
        <div class="flex items-center gap-3">
          <span class="text-sm text-gray-400">View:</span>
          <div class="flex rounded-lg border border-gray-700 bg-dark-secondary p-1">
            <button
              v-for="view in views"
              :key="view.id"
              @click="currentView = view.id"
              :class="[
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                currentView === view.id
                  ? 'bg-primary text-white'
                  : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
              ]"
              :title="view.label"
            >
              <component :is="view.icon" class="h-4 w-4" />
              <span>{{ view.label }}</span>
            </button>
          </div>
        </div>

        <!-- Save All Button Container - Fixed width to prevent layout shifts -->
        <div class="w-[104px]">
          <button
            v-show="hasUnsavedChanges"
            @click="saveAllProducts"
            :disabled="isSaving"
            class="inline-flex h-[38px] w-[104px] items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-all"
            :class="[
              isSaving
                ? 'border-gray-700 bg-gray-800 text-gray-400'
                : 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
            ]"
          >
            <svg
              v-if="isSaving"
              class="h-5 w-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{{ isSaving ? 'Saving' : 'Save All' }}</span>
          </button>
        </div>

        <!-- Sync Button or Settings Link -->
        <router-link
          v-if="!orgStore.isShopifyConnected"
          to="/search-settings"
          class="inline-flex items-center gap-2 rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
        >
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Connect Shopify
        </router-link>

        <!-- Sync Button -->
        <button
          v-else
          class="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-gray-600 hover:text-white disabled:opacity-50"
          @click="syncProducts"
          :disabled="isSyncing || isSaving"
        >
          <svg
            class="h-5 w-5"
            :class="{ 'animate-spin': isSyncing }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ isSyncing ? 'Syncing...' : 'Sync Products' }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-col gap-4 rounded-lg border border-gray-700 bg-dark-secondary p-4">
      <!-- Search and Type Filter -->
      <div class="flex gap-4">
        <!-- Search -->
        <div class="flex-1">
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              v-model="filters.search"
              type="search"
              class="block w-full rounded-lg border border-gray-700 bg-dark pl-10 pr-3 py-2 text-sm text-gray-100 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Search products..."
            />
          </div>
        </div>

        <!-- Product Type Filter -->
        <div class="w-64">
          <select
            v-model="filters.productType"
            class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">All Product Types</option>
            <option v-for="type in productTypes" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </div>

        <!-- Active Status Filter -->
        <div class="flex items-center gap-3 pl-2 border-l border-gray-700">
          <div class="flex rounded-lg border border-gray-700 p-1">
            <button
              v-for="status in statusFilters"
              :key="status.value"
              @click="toggleStatusFilter(status.value)"
              :class="[
                'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-medium transition-colors',
                filters.status === status.value
                  ? 'bg-primary text-white'
                  : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
              ]"
            >
              <span 
                v-if="status.value !== ''"
                class="relative flex h-2 w-2"
              >
                <span :class="[
                  'relative inline-flex h-2 w-2 rounded-full',
                  status.value === 'active' ? 'bg-emerald-500' : 'bg-gray-500'
                ]"></span>
              </span>
              {{ status.label }}
            </button>
          </div>
        </div>

        <!-- Inventory Filter -->
        <div class="flex items-center gap-3 pl-2 border-l border-gray-700">
          <div class="flex rounded-lg border border-gray-700 p-1">
            <button
              v-for="inventory in inventoryFilters"
              :key="inventory.value"
              @click="toggleInventoryFilter(inventory.value)"
              :class="[
                'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-medium transition-colors',
                filters.inventory === inventory.value
                  ? 'bg-primary text-white'
                  : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
              ]"
            >
              <span 
                v-if="inventory.value !== ''"
                class="relative flex h-2 w-2"
              >
                <span :class="[
                  'relative inline-flex h-2 w-2 rounded-full',
                  inventory.value === 'in_stock' ? 'bg-emerald-500' : 'bg-red-500'
                ]"></span>
              </span>
              {{ inventory.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Second Row -->
      <div class="flex items-center gap-4">
        <!-- Tags Filter -->
        <div class="relative tags-filter flex-1">
          <div class="flex flex-wrap items-center gap-2 rounded-lg border border-gray-700 bg-dark p-2 min-h-[40px] focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
            <!-- Selected Tags -->
            <div
              v-for="tag in filters.tags"
              :key="tag"
              class="inline-flex items-center gap-1 rounded-full bg-primary/20 px-2 py-1 text-xs font-medium text-primary"
            >
              {{ tag }}
              <button
                @click="toggleTag(tag)"
                class="text-primary/70 hover:text-primary"
                aria-label="Remove tag"
              >×</button>
            </div>
            
            <!-- Search Input -->
            <input
              v-model="tagSearchQuery"
              type="text"
              class="flex-1 min-w-[120px] bg-transparent text-sm text-gray-100 placeholder-gray-400 focus:outline-none"
              placeholder="Search tags..."
              @focus="showTagSuggestions = true"
              @keydown.backspace="handleBackspace"
            />
          </div>

          <!-- Tag Suggestions Dropdown -->
          <div
            v-if="showTagSuggestions && filteredAvailableTags.length > 0"
            class="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-dark-secondary shadow-lg"
          >
            <button
              v-for="tag in filteredAvailableTags"
              :key="tag"
              @click="selectTag(tag)"
              class="w-full px-3 py-2 text-left text-sm text-gray-200 transition-colors hover:bg-gray-700/50"
            >
              {{ tag }}
            </button>
          </div>
        </div>

        <!-- Shopify Status Filter -->
        <div class="w-48">
          <select
            v-model="filters.shopifyStatus"
            class="block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option 
              v-for="status in shopifyStatusFilters" 
              :key="status.value" 
              :value="status.value"
              :title="['DRAFT', 'ARCHIVED'].includes(status.value) ? 'Product must be activated in Shopify' : ''"
            >
              {{ status.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Active Filters Summary & Bulk Actions -->
      <div v-if="hasActiveFilters" class="flex items-center justify-between border-t border-gray-700 pt-3">
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-400">
            Showing {{ filteredProducts.length }} of {{ products.length }} products
          </span>

          <!-- Bulk Actions -->
          <div v-if="filteredProducts.length > 0" class="flex items-center gap-3 pl-4 border-l border-gray-700">
            <!-- Active/Inactive Buttons -->
            <div class="flex items-center gap-2">
              <button
                @click="bulkUpdateActive(true)"
                :disabled="isBulkUpdating"
                class="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-300 hover:border-emerald-500/30 hover:bg-emerald-500/10 hover:text-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
                Set All Active
              </button>
              <button
                @click="bulkUpdateActive(false)"
                :disabled="isBulkUpdating"
                class="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-300 hover:border-gray-500/30 hover:bg-gray-500/10 hover:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span class="h-2 w-2 rounded-full bg-gray-500"></span>
                Set All Inactive
              </button>
            </div>

            <!-- Priority Input -->
            <div class="flex items-center gap-2 pl-3 border-l border-gray-700">
              <input
                v-model="bulkPriority"
                type="number"
                min="0"
                class="w-20 rounded-lg border border-gray-700 bg-dark px-2 py-1.5 text-sm text-gray-100"
                placeholder="Priority"
                :disabled="isBulkUpdating"
              />
              <button
                @click="bulkUpdatePriority"
                :disabled="isBulkUpdating || !bulkPriority"
                class="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-300 hover:border-primary/30 hover:bg-primary/10 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set All Priority
              </button>
            </div>

            <!-- Progress Indicator -->
            <div v-if="isBulkUpdating" class="flex items-center gap-2 text-sm text-gray-400 pl-3 border-l border-gray-700">
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span v-if="updateProgress.total">
                Updated {{ updateProgress.current }}/{{ updateProgress.total }} products
              </span>
              <span v-else>
                Updating {{ filteredProducts.length }} products...
              </span>
            </div>
          </div>
        </div>

        <button
          @click="clearFilters"
          class="text-sm text-gray-400 hover:text-white"
          :disabled="isBulkUpdating"
        >
          Clear filters
        </button>
      </div>
    </div>

    <!-- Rules Engine -->
    <RulesEngine />

    <!-- Meta Prompt Modal -->
    <MetaPromptModal
      :show="showMetaPromptModal"
      :meta-prompt="selectedProduct?.config?.meta_prompt"
      @close="closeMetaPromptModal"
      @save="saveMetaPrompt"
    />

    <!-- Products Container -->
    <div class="min-h-[200px] rounded-lg border border-gray-700 bg-dark-secondary">
      <!-- Products List/Grid -->
      <div
        :class="[
          currentView === 'grid'
            ? 'grid grid-cols-1 gap-6 p-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'divide-y divide-gray-700'
        ]"
      >
                  <ProductListItem
            v-for="product in filteredProducts"
            :key="product.id"
            :product="product"
            :is-grid="currentView === 'grid'"
            @edit-meta-prompt="openMetaPromptModal(product)"
          />
      </div>

      <!-- Empty State -->
      <div
        v-if="!filteredProducts.length"
        class="flex flex-col items-center justify-center py-12"
      >
        <svg
          class="h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-200">
          {{ products.length ? 'No products match your filters' : 'No products found' }}
        </h3>
        <p class="mt-1 text-sm text-gray-400">
          {{ products.length 
            ? 'Try adjusting your search or filters' 
            : 'Get started by syncing your products from Shopify.' }}
        </p>
        <div class="mt-6">
          <router-link
            v-if="!products.length && !orgStore.isShopifyConnected"
            to="/search-settings"
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Connect Shopify
          </router-link>

          <button
            v-if="!products.length && orgStore.isShopifyConnected"
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
            @click="syncProducts"
            :disabled="isSyncing"
          >
            <svg
              class="h-5 w-5"
              :class="{ 'animate-spin': isSyncing }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ isSyncing ? 'Syncing...' : 'Sync Products' }}
          </button>
          <button
            v-if="hasActiveFilters"
            class="text-sm text-primary hover:text-primary-light"
            @click="clearFilters"
          >
            Clear all filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, defineComponent } from 'vue'
import { useProductStore } from '@/stores/products'
import { useOrgStore } from '@/stores/org'
import ProductListItem from '@/components/content/ProductListItem.vue'
import RulesEngine from '@/components/rules/RulesEngine.vue'
import MetaPromptModal from '@/components/content/MetaPromptModal.vue'

const productStore = useProductStore()
const orgStore = useOrgStore()
const currentView = ref('list')
const isSaving = ref(false)

// Meta prompt modal state
const showMetaPromptModal = ref(false)
const selectedProduct = ref<any>(null)

const openMetaPromptModal = (product: any) => {
  selectedProduct.value = product
  showMetaPromptModal.value = true
}

const closeMetaPromptModal = () => {
  showMetaPromptModal.value = false
  selectedProduct.value = null
}

const saveMetaPrompt = async (metaPrompt: string) => {
  if (!selectedProduct.value) return

  try {
    await productStore.updateProduct(selectedProduct.value.id, {
      config: {
        ...selectedProduct.value.config,
        meta_prompt: metaPrompt
      }
    })
    closeMetaPromptModal()
  } catch (error) {
    console.error('Failed to save meta prompt:', error)
  }
}
const isBulkUpdating = ref(false)
const bulkPriority = ref<number | null>(null)
const updateProgress = ref({ current: 0, total: 0 })

// Filters state
// Tag search state
const tagSearchQuery = ref('')
const showTagSuggestions = ref(false)

// Close tag suggestions when clicking outside
const closeTagSuggestions = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.tags-filter')) {
    showTagSuggestions.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeTagSuggestions)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeTagSuggestions)
})

// Filtered available tags based on search
const filteredAvailableTags = computed(() => {
  if (!tagSearchQuery.value) return availableTags.value
  const query = tagSearchQuery.value.toLowerCase()
  return availableTags.value.filter(
    tag => tag.toLowerCase().includes(query) && !filters.value.tags.includes(tag)
  )
})

const shopifyStatusFilters = [
  { value: 'ACTIVE', label: 'Active in Shopify' },
  { value: 'DRAFT', label: 'Draft in Shopify' },
  { value: 'ARCHIVED', label: 'Archived in Shopify' },
  { value: '', label: 'All Statuses' }
]

const filters = ref({
  search: '',
  productType: '',
  tags: [] as string[],
  status: '', // '' = all, 'active' = active only, 'inactive' = inactive only
  inventory: '', // '' = all, 'in_stock' = in stock only, 'out_of_stock' = out of stock only
  shopifyStatus: 'ACTIVE' // Default to showing active products
})

// Available filter options
const productTypes = computed(() => {
  const types = new Set(products.value
    .map(p => p.product_type)
    .filter(type => type && type.trim() !== '')
  )
  return Array.from(types).sort()
})

const availableTags = computed(() => {
  const tags = new Set(products.value.flatMap(p => p.tags || []))
  return Array.from(tags).sort()
})

const statusFilters = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
]

const inventoryFilters = [
  { value: '', label: 'All' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'out_of_stock', label: 'Out of Stock' }
]

const toggleStatusFilter = (status: string) => {
  filters.value.status = filters.value.status === status ? '' : status
}

const toggleInventoryFilter = (inventory: string) => {
  filters.value.inventory = filters.value.inventory === inventory ? '' : inventory
}

// Update hasActiveFilters computed
const hasActiveFilters = computed(() => {
  return filters.value.search || 
         filters.value.productType || 
         filters.value.tags.length > 0 ||
         filters.value.status !== '' ||
         filters.value.inventory !== ''
})

const hasUnsavedChanges = computed(() => productStore.hasUnsavedChanges)

// Filter methods
const selectTag = (tag: string) => {
  if (!filters.value.tags.includes(tag)) {
    filters.value.tags.push(tag)
  }
  tagSearchQuery.value = ''
  showTagSuggestions.value = true // Keep suggestions visible after selection
  // Keep focus on input after selection
  const input = document.querySelector('.tags-filter input') as HTMLInputElement
  if (input) {
    input.focus()
  }
}

const handleBackspace = () => {
  if (tagSearchQuery.value === '' && filters.value.tags.length > 0) {
    const lastTag = filters.value.tags[filters.value.tags.length - 1]
    toggleTag(lastTag)
  }
}

const toggleTag = (tag: string) => {
  const index = filters.value.tags.indexOf(tag)
  if (index === -1) {
    filters.value.tags.push(tag)
  } else {
    filters.value.tags.splice(index, 1)
  }
  tagSearchQuery.value = ''
  showTagSuggestions.value = true // Keep suggestions visible after removing tag
  // Keep focus on input after removing tag
  const input = document.querySelector('.tags-filter input') as HTMLInputElement
  if (input) {
    input.focus()
  }
}

const clearFilters = () => {
  filters.value = {
    search: '',
    productType: '',
    tags: [],
    status: '',
    inventory: '',
    shopifyStatus: 'ACTIVE'
  }
}

// Filtered products
const filteredProducts = computed(() => {
  let result = [...products.value]

  // Text search
  if (filters.value.search) {
    const searchLower = filters.value.search.toLowerCase()
    result = result.filter(product => 
      product.title.toLowerCase().includes(searchLower) ||
      product.product_type.toLowerCase().includes(searchLower) ||
      product.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    )
  }

  // Product type filter
  if (filters.value.productType) {
    result = result.filter(product => 
      product.product_type === filters.value.productType
    )
  }

  // Tags filter
  if (filters.value.tags.length) {
    result = result.filter(product => 
      filters.value.tags.every(tag => product.tags?.includes(tag))
    )
  }

      // Status filter
  if (filters.value.status) {
    result = result.filter(product => 
      filters.value.status === 'active' ? product.config?.is_active === true : product.config?.is_active === false
    )
  }

  // Inventory filter
  if (filters.value.inventory) {
    result = result.filter(product => {
      const hasStock = (product.inventory_qty ?? 0) > 0
      return filters.value.inventory === 'in_stock' ? hasStock : !hasStock
    })
  }

  // Shopify status filter
  if (filters.value.shopifyStatus) {
    result = result.filter(product => 
      product.shopify_status === filters.value.shopifyStatus
    )
  }

  return result
})

const saveAllProducts = async () => {
  if (isSaving.value) return
  
  isSaving.value = true
  try {
    await productStore.saveAllProducts()
  } catch (error) {
    console.error('Failed to save all products:', error)
  } finally {
    isSaving.value = false
  }
}

const bulkUpdateActive = async (isActive: boolean) => {
  if (isBulkUpdating.value || !filteredProducts.value.length) return

  isBulkUpdating.value = true
  updateProgress.value = { current: 0, total: 0 }

  try {
    const productIds = filteredProducts.value.map(p => p.id)
    await productStore.batchUpdateProducts(
      productIds,
      { config: { is_active: isActive, priority: 0 } },
      (current, total) => {
        updateProgress.value = { current, total }
      }
    )
  } catch (error) {
    console.error('Failed to update product status:', error)
  } finally {
    isBulkUpdating.value = false
    updateProgress.value = { current: 0, total: 0 }
  }
}

const bulkUpdatePriority = async () => {
  if (isBulkUpdating.value || !filteredProducts.value.length || !bulkPriority.value) return

  isBulkUpdating.value = true
  updateProgress.value = { current: 0, total: 0 }

  try {
    const productIds = filteredProducts.value.map(p => p.id)
    await productStore.batchUpdateProducts(
      productIds,
      { config: { priority: bulkPriority.value, is_active: true } },
      (current, total) => {
        updateProgress.value = { current, total }
      }
    )
    bulkPriority.value = null
  } catch (error) {
    console.error('Failed to update product priority:', error)
  } finally {
    isBulkUpdating.value = false
    updateProgress.value = { current: 0, total: 0 }
  }
}

const views = [
  {
    id: 'list',
    label: 'List',
    icon: defineComponent({
      template: `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
      `
    })
  },
  {
    id: 'grid',
    label: 'Grid',
    icon: defineComponent({
      template: `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
        </svg>
      `
    })
  }
]

// Computed properties from store
const products = computed(() => productStore.products)
const sortedProducts = computed(() => productStore.sortedProducts)
const isSyncing = computed(() => productStore.isSyncing)
const lastSyncedText = computed(() => {
  if (!productStore.lastSyncTime) return 'Never'
  
  const diffInMinutes = Math.round((Date.now() - productStore.lastSyncTime.getTime()) / (1000 * 60))
  
  if (diffInMinutes >= 60) {
    const diffInHours = Math.round(diffInMinutes / 60)
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInHours, 'hours')
  }
  
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-diffInMinutes, 'minutes')
})

// Methods
const syncProducts = async () => {
  try {
    const result = await productStore.syncWithShopify()
    
    // Optional: You could add a toast notification here to show the sync status
    if (result.sync_status === 'pending' || result.sync_status === 'syncing') {
      console.log('Sync in progress:', result.message)
    } else if (result.sync_status === 'completed') {
      console.log('Sync completed successfully')
    }
  } catch (error) {
    console.error('Failed to sync products:', error)
    // Optional: You could add error handling UI feedback here
  }
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    productStore.fetchProducts(),
    orgStore.watchOrgData()
  ])
})

// Cleanup subscriptions when component is unmounted
onBeforeUnmount(() => {
  if (productStore.unsubscribeSyncStatus) {
    productStore.unsubscribeSyncStatus()
    productStore.unsubscribeSyncStatus = null
  }
  orgStore.cleanup()
})
</script> 